import isEqual from 'lodash/isEqual';
import { createSelector } from 'reselect';

import { stripHtmlTags } from '@app/utils/stripHtmlTags';
import { skuLeckerliesRegex } from '@constants/common';
import { isHiddenRecipe, RECIPES } from '@constants/subscription';
import { getUpdatedSubscriptionOptions } from '@modules/settings/core/selectors';
import { RootState } from '@store/index';

import { getDisabledRecipes } from '../utils/getDisabledRecipes';
import { filterAdditionalLines, findFreshLine } from '../utils/lineItems';
import { buildStoreCreditByVariant } from '../utils/storeCredit';
import {
  discountVariantFromCode,
  isItemScopedDiscount,
  isStoreCreditDiscountCode,
} from './discountLabel';

const getSettings = (state: RootState) => state.settings.data;

const getPending = (state: RootState) => state.subscriptions.pending;

const getSubscriptions = (state: RootState) => state.subscriptions.data;

const getError = (state: RootState) => state.subscriptions.error;

export const getPendingSelector = createSelector(getPending, (pending) => pending);

export const getLoadingMessageSelector = (state: RootState) => state.subscriptions.loadingMessage;

export const getErrorSelector = createSelector(getError, (error) =>
  typeof error === 'string' ? error : null,
);

export const getFlowBannerErrorSelector = createSelector(getError, (error) => {
  if (error && typeof error === 'object' && 'type' in error) {
    return (error as { type: string; message: string }).type === 'FLOW_BANNER_NOT_FOUND'
      ? (error as { type: string; message: string }).message
      : null;
  }
  return null;
});

export const getSubscriptionsSelector = createSelector(getSubscriptions, (subscriptions) =>
  subscriptions.filter(
    (subscription: ISubscription) =>
      subscription.lines?.some((lineItem: any) => lineItem != null && lineItem.product !== null) &&
      subscription.pet !== null,
  ),
);

export const getCancelledSubscriptionsSelector = createSelector(getSubscriptions, (subscriptions) =>
  subscriptions.filter((subscription: ISubscription) => subscription?.status === 'CANCELLED'),
);

export const getDefaultSubscriptionSelector = createSelector(
  [getSubscriptions, getSettings],
  (subscriptions, settings) => {
    return subscriptions?.find(
      (subscription: ISubscription) => subscription?.id === settings.defaultSubscription,
    );
  },
);

export const getPetSelector = createSelector(
  [getDefaultSubscriptionSelector],
  (defaultSubscription) => defaultSubscription?.pet,
);

export const getDiscountSelector = createSelector(
  [getDefaultSubscriptionSelector],
  (defaultSubscription) =>
    defaultSubscription?.discounts?.filter((discount: Discount) => discount.isActive)?.[0],
);

export const getDiscountsSelector = createSelector(
  [getDefaultSubscriptionSelector],
  (defaultSubscription) =>
    defaultSubscription?.discounts?.filter((discount: Discount) => discount.isActive) ?? [],
);

// Discounts that apply to the subscription as a whole (user-entered promo
// codes). Item-scoped redemptions — Pfotenpunkte (SCV…) and Sommer Aktion
// (PS50…) codes that carry a variant id — are excluded; those render on the
// funded shop item instead of in the discount list.
export const getSubscriptionLevelDiscountsSelector = createSelector(
  [getDiscountsSelector],
  (discounts) => discounts.filter((discount: Discount) => !isItemScopedDiscount(discount.title)),
);

// Maps `variantShopifyId -> discounts[]` for item-scoped discounts so a line
// item card can show the reductions (Pfotenpunkte, Sommer Aktion) tied to it.
export const getItemDiscountsByVariantSelector = createSelector(
  [getDiscountsSelector],
  (discounts) => {
    const map: Record<string, Discount[]> = {};
    for (const discount of discounts) {
      const variant = discountVariantFromCode(discount.title);
      if (!variant) continue;
      (map[variant] ??= []).push(discount);
    }
    return map;
  },
);

// Maps `variantShopifyId -> redeemed amount` so order/line views can show the
// reduction Loop doesn't pre-apply to the line preview.
export const getStoreCreditByVariantSelector = createSelector(
  [getDefaultSubscriptionSelector],
  (defaultSubscription) => buildStoreCreditByVariant(defaultSubscription?.discounts),
);

export const getSubscriptionsWithPetsSortedByLF = createSelector(
  [getSubscriptionsSelector],
  (subscriptions) => {
    const sortedSubscriptions = [...subscriptions].sort((subA, subB) =>
      subA?.status === 'ACTIVE' ? -1 : subB?.status === 'ACTIVE' ? 1 : 0,
    );

    return sortedSubscriptions.filter(
      (subscription) => subscription?.pet && subscription?.pet.name,
    );
  },
);

export const getLineItemSelector = createSelector(
  [getDefaultSubscriptionSelector],
  (defaultSubscription) => findFreshLine(defaultSubscription?.lines),
);

export const getAdditionalItemsSelector = createSelector(
  [getDefaultSubscriptionSelector],
  (defaultSubscription) => filterAdditionalLines(defaultSubscription?.lines),
);

export const getLeckerlieSelector = createSelector(
  [getDefaultSubscriptionSelector],
  (defaultSubscription) => {
    return (
      defaultSubscription?.lines?.filter((lineItem: any) =>
        skuLeckerliesRegex.test(lineItem.sku),
      ) || []
    );
  },
);

export const getSubscriptionsShippingAddresses = createSelector(getSubscriptions, (subscriptions) =>
  subscriptions
    .map((subscription: ISubscription) => subscription.shippingAddress)
    .filter(
      (address: IShippingAddress, index: number, self: IShippingAddress[]) =>
        index === self.findIndex((t) => t.address1 === address.address1 && t.zip === address.zip),
    ),
);

export const getDefaultSubscriptionPaymentIdSelector = createSelector(
  [getDefaultSubscriptionSelector],
  (defaultSubscription) => defaultSubscription?.paymentMethodId,
);

// SKU shape: `‹4-digit-kcal›-‹recipes›-‹pension H|V›-‹cycle in days›`, e.g.
// `0161-CGGS-V-28`. The trailing segment used to be limited to 14/28/TEST, but
// selling plans are now dynamic (21/35/42/56/…). These regexes accept any 1–3
// digit day count so new plans parse correctly; 14/28/TEST behave exactly as
// before. `TEST` still maps to a 14-day cycle for the legacy test package.
export const getSubscriptionCycleSelector = createSelector([getLineItemSelector], (lineItem) => {
  const cycle = lineItem?.sku?.match(/\d{4}-\w*-[HV]-(\d{1,3}|TEST)/);

  return cycle && cycle[1] === 'TEST' ? 14 : parseInt(cycle?.[1] || '14', 10);
});

export const getSubscriptionPensionSelector = createSelector([getLineItemSelector], (lineItem) => {
  const pension = lineItem?.sku?.match(/\d{4}-\w*-([HV])-(?:\d{1,3}|TEST)/);

  return pension?.[1] || '';
});

export const getSubscriptionRecipesSelector = createSelector([getLineItemSelector], (lineItem) => {
  const recipesMatch = lineItem?.sku?.match(/\d{4}-(\w*)-[HV]-(?:\d{1,3}|TEST)/);

  return recipesMatch
    ? recipesMatch[1]
        ?.match(/.{1,2}/g)
        ?.map((key: string) => RECIPES[key as keyof typeof RECIPES])
        // Hidden recipes (e.g. WS) are stripped so they never appear selected.
        .filter((recipe) => recipe && !isHiddenRecipe(recipe)) || []
    : [];
});

export const getDisabledRecipesSelector = createSelector([getPetSelector], (pet) =>
  pet ? getDisabledRecipes(pet) : [],
);

export const getSubscriptionOptionsSelector = createSelector(
  [getSubscriptionCycleSelector, getSubscriptionPensionSelector, getSubscriptionRecipesSelector],
  (cycle, pension, recipes) => ({
    cycle,
    pension,
    recipes,
  }),
);

export const getIsUpdatedVariantSelector = createSelector(
  [getSubscriptionOptionsSelector, getUpdatedSubscriptionOptions],
  (subscriptionOptions, updatedSubscriptionOptions) =>
    !isEqual(subscriptionOptions, updatedSubscriptionOptions),
);

export const getUpdatedSubscriptionValuesSelector = createSelector(
  [getSubscriptionOptionsSelector, getUpdatedSubscriptionOptions],
  (subscriptionOptions: any, updatedSubscriptionOptions) => {
    const updatedValues: any = {};

    Object.keys(subscriptionOptions).forEach((key) => {
      if (!isEqual(subscriptionOptions[key], updatedSubscriptionOptions?.[key]))
        updatedValues[key] = updatedSubscriptionOptions?.[key];
    });

    return updatedValues;
  },
);

export const getFlowBannerSelector = createSelector(
  [getDefaultSubscriptionSelector],
  (defaultSubscription) => defaultSubscription?.flowBanner,
);
export const getFlowBannerTextSelector = createSelector(
  [getDefaultSubscriptionSelector],
  (defaultSubscription) => stripHtmlTags(defaultSubscription?.flowBanner?.[0] ?? ''),
);

export const getCancellationOfferSelector = createSelector(
  [getDefaultSubscriptionSelector],
  (defaultSubscription) => defaultSubscription?.cancellationOffer,
);

export const getCancellationFlowIdSelector = createSelector(
  [getDefaultSubscriptionSelector],
  (defaultSubscription) => defaultSubscription?.cancellationFlowId,
);

export const getCurrencyCodeSelector = createSelector(
  [getDefaultSubscriptionSelector],
  (defaultSubscription) => {
    const currencyCode = defaultSubscription?.currencyCode;

    if (currencyCode === 'EUR') {
      return '€';
    }

    return currencyCode || '€';
  },
);

const getPetProfileUpdate = (state: RootState) => state.subscriptions.petProfileUpdate;

export const getPetProfileUpdateSelector = createSelector(
  getPetProfileUpdate,
  (petProfileUpdate) => petProfileUpdate,
);

export const getRecalculatedProductSelector = createSelector(
  getPetProfileUpdate,
  (petProfileUpdate) => petProfileUpdate?.recalculatedProduct,
);

export const getIsRecalculatingSelector = createSelector(
  getPetProfileUpdate,
  (petProfileUpdate) => petProfileUpdate?.isRecalculating || false,
);

export const getIsUpdatingPetProfileSelector = createSelector(
  getPetProfileUpdate,
  (petProfileUpdate) => petProfileUpdate?.isUpdating || false,
);

export const getPetProfileUpdateErrorSelector = createSelector(
  getPetProfileUpdate,
  (petProfileUpdate) => petProfileUpdate?.error,
);

export const getAllergiesSelector = (state: RootState) => state.subscriptions.allergies;

export const getHealthIssuesSelector = (state: RootState) => state.subscriptions.healthIssues;
